import {
  Box,
  Check,
  ChevronDown,
  Cloud,
  Download,
  ImagePlus,
  Layers3,
  MousePointer2,
  Move,
  RotateCw,
  Ruler,
  Sparkles,
  Trash2,
  Upload,
  WandSparkles,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { assetLibrary, initialFurniture, initialRooms, initialWalls } from "./data";
import { parseFloorPlan } from "./planParser";
import { Plan2D } from "./Plan2D";
import type { AssetKind, Furniture, ViewMode, Wall } from "./types";

const STORAGE_KEY = "roomform-project-v1";
const Scene3D = lazy(() => import("./Scene3D").then((module) => ({ default: module.Scene3D })));

export default function App() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("2d");
  const [walls, setWalls] = useState<Wall[]>(initialWalls);
  const [rooms, setRooms] = useState(initialRooms);
  const [furniture, setFurniture] = useState<Furniture[]>(initialFurniture);
  const [selectedWallId, setSelectedWallId] = useState<string | null>("w7");
  const [selectedFurnitureId, setSelectedFurnitureId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("Hellerup house");
  const [sourcePreview, setSourcePreview] = useState<string | null>(null);
  const [parseState, setParseState] = useState<{ active: boolean; message: string; percent: number }>({
    active: false,
    message: "",
    percent: 0,
  });
  const [saved, setSaved] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      const project = JSON.parse(stored) as {
        walls?: Wall[];
        rooms?: typeof initialRooms;
        furniture?: Furniture[];
        projectName?: string;
      };
      if (project.walls?.length) setWalls(project.walls);
      if (project.rooms) setRooms(project.rooms);
      if (project.furniture) setFurniture(project.furniture);
      if (project.projectName) setProjectName(project.projectName);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ walls, rooms, furniture, projectName }));
      setSaved(true);
    }, 500);
    setSaved(false);
    return () => window.clearTimeout(timer);
  }, [walls, rooms, furniture, projectName]);

  const selectedWall = useMemo(
    () => walls.find((wall) => wall.id === selectedWallId) ?? null,
    [walls, selectedWallId],
  );
  const selectedFurniture = useMemo(
    () => furniture.find((item) => item.id === selectedFurnitureId) ?? null,
    [furniture, selectedFurnitureId],
  );

  const changeWall = (wall: Wall) => {
    setWalls((current) => current.map((entry) => (entry.id === wall.id ? wall : entry)));
  };

  const setWallLength = (raw: number) => {
    if (!selectedWall || !Number.isFinite(raw) || raw < 0.2) return;
    const dx = selectedWall.x2 - selectedWall.x1;
    const dy = selectedWall.y2 - selectedWall.y1;
    const current = Math.hypot(dx, dy) || 1;
    changeWall({
      ...selectedWall,
      x2: selectedWall.x1 + (dx / current) * raw,
      y2: selectedWall.y1 + (dy / current) * raw,
      label: raw.toFixed(2),
    });
  };

  const updateFurniture = (id: string, patch: Partial<Furniture>) => {
    setFurniture((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const addAsset = (kind: AssetKind, x = 8.8, y = 2.4) => {
    const asset = assetLibrary.find((entry) => entry.kind === kind);
    if (!asset) return;
    const id = `${kind}-${Date.now()}`;
    setFurniture((current) => [...current, { ...asset, id, x, y, rotation: 0 }]);
    setSelectedFurnitureId(id);
    setSelectedWallId(null);
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    if (sourcePreview) URL.revokeObjectURL(sourcePreview);
    setSourcePreview(URL.createObjectURL(file));
    setParseState({ active: true, message: "Preparing image", percent: 5 });
    try {
      const result = await parseFloorPlan(file, (message, percent) =>
        setParseState({ active: true, message, percent }),
      );
      setWalls(result.walls);
      setRooms([]);
      setFurniture([]);
      setSelectedWallId(result.walls[0]?.id ?? null);
      setProjectName(file.name.replace(/\.[^.]+$/, ""));
      window.setTimeout(() => setParseState((state) => ({ ...state, active: false })), 900);
    } catch {
      setParseState({ active: true, message: "Could not read this image. Try a clearer PNG or JPG.", percent: 100 });
      window.setTimeout(() => setParseState((state) => ({ ...state, active: false })), 2400);
    }
  };

  const restoreSample = () => {
    setWalls(initialWalls);
    setRooms(initialRooms);
    setFurniture(initialFurniture);
    setProjectName("Hellerup house");
    setSelectedWallId("w7");
    setSelectedFurnitureId(null);
  };

  const totalArea = rooms.reduce((sum, room) => sum + room.width * room.depth, 0);
  const exportProject = () => {
    const blob = new Blob(
      [JSON.stringify({ version: 1, name: projectName, walls, rooms, furniture }, null, 2)],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${projectName.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "roomform"}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark"><Layers3 size={19} strokeWidth={2.4} /></span>
          <span>roomform</span>
          <span className="beta">BETA</span>
        </div>
        <div className="project-title">
          <input value={projectName} onChange={(event) => setProjectName(event.target.value)} aria-label="Project name" />
          <span className="project-meta">{walls.length} walls · {furniture.length} objects</span>
        </div>
        <div className="header-actions">
          <span className={`save-status ${saved ? "is-saved" : ""}`}><Cloud size={15} /> {saved ? "Saved" : "Saving…"}</span>
          <button className="ghost-button" title="Export project" onClick={exportProject}><Download size={17} /> Export</button>
          <button className="avatar" aria-label="Account menu">TH</button>
        </div>
      </header>

      <div className="workspace">
        <aside className="left-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">PROJECT</span>
              <h2>Build your space</h2>
            </div>
          </div>

          <button className="import-card" onClick={() => fileInputRef.current?.click()}>
            <span className="import-icon"><ImagePlus size={21} /></span>
            <span>
              <strong>Import a floor plan</strong>
              <small>PNG, JPG · numbers read automatically</small>
            </span>
            <Upload size={17} />
          </button>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => event.target.files?.[0] && handleUpload(event.target.files[0])}
          />

          {sourcePreview && (
            <div className="source-preview">
              <img src={sourcePreview} alt="Imported floor plan" />
              <span><Check size={13} /> Source attached</span>
            </div>
          )}

          <div className="library-header">
            <span className="eyebrow">LOW-POLY LIBRARY</span>
            <span>{assetLibrary.length} items</span>
          </div>
          <p className="library-help">Drag an object onto the plan, or click to place it.</p>
          <div className="asset-grid">
            {assetLibrary.map((asset) => (
              <button
                key={asset.kind}
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData("asset-kind", asset.kind);
                  event.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => addAsset(asset.kind)}
                className="asset-card"
              >
                <span className={`asset-preview asset-${asset.kind}`} style={{ "--asset-color": asset.color } as React.CSSProperties}>
                  <span />
                </span>
                <strong>{asset.name}</strong>
                <small>{asset.width.toFixed(1)} × {asset.depth.toFixed(1)} m</small>
              </button>
            ))}
          </div>
          <div className="tip-card">
            <Sparkles size={17} />
            <span><strong>Tip</strong> Select an orange dimension to type its true wall length.</span>
          </div>
        </aside>

        <main className="stage">
          <div className="stage-toolbar">
            <div className="tool-group">
              <button className="tool-button is-active"><MousePointer2 size={17} /> Select</button>
              <button className="tool-button"><Move size={17} /> Move</button>
              <span className="toolbar-divider" />
              <span className="area-readout"><Ruler size={16} /> {totalArea ? `${totalArea.toFixed(1)} m² mapped` : `${walls.length} walls mapped`}</span>
            </div>
            <div className="view-toggle" aria-label="View mode">
              <button className={viewMode === "2d" ? "is-active" : ""} onClick={() => setViewMode("2d")}>
                <span>2D</span> Plan
              </button>
              <button className={viewMode === "3d" ? "is-active" : ""} onClick={() => setViewMode("3d")}>
                <Box size={16} /> 3D
              </button>
            </div>
          </div>

          <div className="canvas-wrap">
            {viewMode === "2d" ? (
              <Plan2D
                walls={walls}
                rooms={rooms}
                furniture={furniture}
                selectedWallId={selectedWallId}
                selectedFurnitureId={selectedFurnitureId}
                onSelectWall={setSelectedWallId}
                onSelectFurniture={setSelectedFurnitureId}
                onChangeWall={changeWall}
                onMoveFurniture={(id, x, y) => updateFurniture(id, { x, y })}
                onAddAsset={addAsset}
              />
            ) : (
              <Suspense fallback={<div className="scene-loading"><Box size={24} /> Building 3D view…</div>}>
                <Scene3D
                  walls={walls}
                  rooms={rooms}
                  furniture={furniture}
                  selectedWallId={selectedWallId}
                  selectedFurnitureId={selectedFurnitureId}
                  onSelectWall={setSelectedWallId}
                  onSelectFurniture={setSelectedFurnitureId}
                  onAddAsset={addAsset}
                />
              </Suspense>
            )}

            {parseState.active && (
              <div className="parse-overlay">
                <div className="parse-dialog">
                  <span className="parse-icon"><WandSparkles size={24} /></span>
                  <span className="eyebrow">PLAN READER</span>
                  <h3>{parseState.message}</h3>
                  <div className="progress-track"><span style={{ width: `${parseState.percent}%` }} /></div>
                  <p>{parseState.percent}% · detecting high-contrast walls and printed measurements</p>
                </div>
              </div>
            )}
          </div>
        </main>

        <aside className="right-panel">
          <div className="inspector-heading">
            <div>
              <span className="eyebrow">INSPECTOR</span>
              <h2>{selectedWall ? "Wall" : selectedFurniture ? selectedFurniture.name : "Nothing selected"}</h2>
            </div>
            <ChevronDown size={18} />
          </div>

          {selectedWall && (
            <div className="inspector-content">
              <div className="selection-preview wall-preview">
                <span />
                <small>{selectedWall.id.toUpperCase()}</small>
              </div>
              <label className="field-label">Wall length</label>
              <div className="measure-input">
                <button onClick={() => setWallLength((Number(selectedWall.label) || Math.hypot(selectedWall.x2 - selectedWall.x1, selectedWall.y2 - selectedWall.y1)) - 0.1)}>−</button>
                <input
                  type="number"
                  aria-label="Wall length"
                  min="0.2"
                  step="0.01"
                  value={selectedWall.label ?? Math.hypot(selectedWall.x2 - selectedWall.x1, selectedWall.y2 - selectedWall.y1).toFixed(2)}
                  onChange={(event) => setWallLength(Number(event.target.value))}
                />
                <span>m</span>
                <button onClick={() => setWallLength((Number(selectedWall.label) || Math.hypot(selectedWall.x2 - selectedWall.x1, selectedWall.y2 - selectedWall.y1)) + 0.1)}>+</button>
              </div>
              <p className="field-help">The endpoint moves while the wall keeps its current angle.</p>
              <div className="two-fields">
                <label><span>Height</span><div><input type="number" step="0.1" value={selectedWall.height} onChange={(event) => changeWall({ ...selectedWall, height: Number(event.target.value) })} /><small>m</small></div></label>
                <label><span>Thickness</span><div><input type="number" step="0.01" value={selectedWall.thickness} onChange={(event) => changeWall({ ...selectedWall, thickness: Number(event.target.value) })} /><small>m</small></div></label>
              </div>
              <div className="coordinate-card">
                <span>START <b>{selectedWall.x1.toFixed(2)}, {selectedWall.y1.toFixed(2)}</b></span>
                <span>END <b>{selectedWall.x2.toFixed(2)}, {selectedWall.y2.toFixed(2)}</b></span>
              </div>
              <button className="danger-button" onClick={() => { setWalls((current) => current.filter((wall) => wall.id !== selectedWall.id)); setSelectedWallId(null); }}>
                <Trash2 size={16} /> Delete wall
              </button>
            </div>
          )}

          {selectedFurniture && (
            <div className="inspector-content">
              <div className="selection-preview furniture-preview" style={{ "--selected-color": selectedFurniture.color } as React.CSSProperties}>
                <Box size={28} />
                <small>{selectedFurniture.kind.toUpperCase()}</small>
              </div>
              <label className="field-label">Position</label>
              <div className="two-fields">
                <label><span>X</span><div><input type="number" step="0.1" value={selectedFurniture.x.toFixed(2)} onChange={(event) => updateFurniture(selectedFurniture.id, { x: Number(event.target.value) })} /><small>m</small></div></label>
                <label><span>Y</span><div><input type="number" step="0.1" value={selectedFurniture.y.toFixed(2)} onChange={(event) => updateFurniture(selectedFurniture.id, { y: Number(event.target.value) })} /><small>m</small></div></label>
              </div>
              <label className="field-label">Rotation</label>
              <div className="rotation-control">
                <button onClick={() => updateFurniture(selectedFurniture.id, { rotation: selectedFurniture.rotation - 15 })}><RotateCw size={16} className="flip" /></button>
                <input type="range" min="-180" max="180" value={selectedFurniture.rotation} onChange={(event) => updateFurniture(selectedFurniture.id, { rotation: Number(event.target.value) })} />
                <span>{selectedFurniture.rotation}°</span>
                <button onClick={() => updateFurniture(selectedFurniture.id, { rotation: selectedFurniture.rotation + 15 })}><RotateCw size={16} /></button>
              </div>
              <label className="field-label">Finish</label>
              <div className="swatches">
                {["#507A6A", "#C9784C", "#7E8FA3", "#BD885D", "#363F3B"].map((color) => (
                  <button key={color} style={{ background: color }} className={selectedFurniture.color === color ? "is-active" : ""} onClick={() => updateFurniture(selectedFurniture.id, { color })} aria-label={`Use color ${color}`} />
                ))}
              </div>
              <button className="danger-button" onClick={() => { setFurniture((current) => current.filter((item) => item.id !== selectedFurniture.id)); setSelectedFurnitureId(null); }}>
                <Trash2 size={16} /> Remove object
              </button>
            </div>
          )}

          {!selectedWall && !selectedFurniture && (
            <div className="empty-inspector">
              <span><MousePointer2 size={24} /></span>
              <h3>Select an element</h3>
              <p>Choose a wall dimension or furniture object to edit its details.</p>
            </div>
          )}

          <div className="inspector-footer">
            <button onClick={restoreSample}>Restore sample</button>
            <span>Local autosave</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
